import React, { useState, useRef, useEffect, useCallback, forwardRef, useImperativeHandle } from 'react';
import {
  DndContext,
  closestCenter,
  KeyboardSensor,
  PointerSensor,
  useSensor,
  useSensors,
} from '@dnd-kit/core';
import {
  arrayMove,
  SortableContext,
  sortableKeyboardCoordinates,
  useSortable,
  verticalListSortingStrategy,
} from '@dnd-kit/sortable';
import { CSS } from '@dnd-kit/utilities';
import { restrictToVerticalAxis, restrictToParentElement } from '@dnd-kit/modifiers';
import { useMobile } from '../../hooks';
import { searchLocation } from '../../services/api';
import { formatDistance, formatDuration } from '../../utils/helpers';
import { SEARCH_CONFIG } from '../../utils/constants';
import RouteDetailsPanel from '../RouteDetails/RouteDetailsPanel';

// Sortable Waypoint Item Component - handles source, stops, and destination
const SortableWaypointItem = ({ 
  waypoint,
  index,
  totalCount,
  inputRef,
  onInputChange,
  onInputKeyDown,
  onInputFocus,
  onInputBlur,
  onRemove,
  isLoading,
  isActive,
  showDropdown,
  searchResults,
  selectedResultIndex,
  onResultSelect,
  setSelectedResultIndex,
  listeners,
  attributes,
  setNodeRef,
  style,
  isDragging,
  isFirst,
  isLast,
}) => {
  // Determine icon based on position and content
  const getIcon = () => {
    if (waypoint.name === 'Your Location') return "/icons/myLocationIcon.png";
    return waypoint.location ? "/icons/homeWorkIcon.png" : "/icons/locationInputIcon.png";
  };

  // Determine placeholder based on position
  const getPlaceholder = () => {
    if (isFirst) return "Search origin or click on the map";
    if (isLast && totalCount > 1) return "Search stop or click on the map";
    return "Search stop or click on the map";
  };

  // Show cross icon on every box when there are more than 2 waypoints
  const canRemove = totalCount > 2;

  return (
    <div ref={setNodeRef} style={style} className="sortable-waypoint-wrapper">
      {index > 0 && <img className="route-dashed-line-vertical" src="/icons/dashedIconRoute.png" alt="" />}
      <div className={`route-input-row route-waypoint-row${isDragging ? ' dragging' : ''}`}>
        <div className="route-input-with-icon">
          <div className="route-input-icon">
            <img 
              src={getIcon()} 
              alt={isFirst ? "Origin" : "Waypoint"} 
              className={isFirst ? "my-location-icon" : ""}
              style={{ width: '18px', height: '18px', objectFit: 'contain' }} 
            />
          </div>
          <input
            ref={inputRef}
            type="text"
            className={`route-search-input-new${isFirst && waypoint.name === 'Your Location' ? ' route-search-input-prefilled' : ''}${totalCount <= 2 ? ' route-search-input-full-width' : ''}`}
            placeholder={getPlaceholder()}
            value={waypoint.name || ''}
            onChange={onInputChange}
            onKeyDown={onInputKeyDown}
            onFocus={onInputFocus}
            onBlur={onInputBlur}
          />
          {isLoading && isActive && (
            <div className="route-search-loader-new">
              <div className="spinner-tiny"></div>
            </div>
          )}
          {totalCount > 2 && (
          <div className="route-stop-actions">
            <div
              className="route-stop-drag-handle"
              {...attributes}
              {...listeners}
            >
              <img
                src="/icons/drag-indicator copy.png"
                alt="Drag to reorder"
                className="route-stop-drag-icon"
              />
            </div>
            {canRemove && (
              <img
                src="/icons/Close.svg"
                alt="Remove"
                className="route-stop-close-icon"
                onClick={onRemove}
              />
            )}
          </div>
          )}
        </div>
      </div>
    </div>
  );
};

// Wrapper component that uses useSortable
const DraggableWaypoint = (props) => {
  const {
    attributes,
    listeners,
    setNodeRef,
    transform,
    transition,
    isDragging,
  } = useSortable({ id: props.waypoint.id });

  const style = {
    transform: CSS.Transform.toString(transform),
    transition,
    opacity: isDragging ? 0.5 : 1,
    zIndex: isDragging ? 1000 : 1,
    position: 'relative',
    backgroundColor: isDragging ? '#f0f7ff' : '#fff',
    borderRadius: isDragging ? '8px' : '0',
    boxShadow: isDragging ? '0 4px 12px rgba(35, 150, 251, 0.3)' : 'none',
  };

  return (
    <SortableWaypointItem
      {...props}
      listeners={listeners}
      attributes={attributes}
      setNodeRef={setNodeRef}
      style={style}
      isDragging={isDragging}
    />
  );
};

// SortableStopItem removed (unused legacy component)

const RoutingPanel = forwardRef(({
  isRoutingMode,
  routeDetails,
  routeDetailsLoading,
  routePointA,
  routePointB,
  routePointADetails,
  routePointBDetails,
  mapRef,
  onClose,
  onRoutePointsChange,
  onStopsChange,
  onPoiClick,
  prefillDestinationName = '',
  userLocation,
  focusTarget = 'source',
  onActiveInputChange,
  travelMode: externalTravelMode,
  onTravelModeChange,
  onSelectRoute,
  selectedRouteIndex = 0,
  onStepHover,
  onStepLeave,
  onStepClick
}, ref) => {
  const isMobile = useMobile();
  const isMweb = isMobile || (typeof document !== 'undefined' && document.body.classList.contains('mweb'));
  const [sourceResults, setSourceResults] = useState([]);
  const [destResults, setDestResults] = useState([]);
  const [sourceLoading] = useState(false);
  const [destLoading] = useState(false);
  const [showSourceDropdown, setShowSourceDropdown] = useState(false);
  const [showDestDropdown, setShowDestDropdown] = useState(false);
  const [activeInput, setActiveInput] = useState(null); // 'source' or 'dest'
  const [sourceCleared, setSourceCleared] = useState(false);
  // Keyboard navigation indices
  const [sourceSelectedIndex, setSourceSelectedIndex] = useState(0);
  const [destSelectedIndex, setDestSelectedIndex] = useState(0);

  const [sheetHeight, setSheetHeight] = useState(40);
  const startY = useRef(null);
  const startHeight = useRef(null);

  // Route Details Panel state
  const [showRouteDetails, setShowRouteDetails] = useState(false);
  const [detailsRouteIndex, setDetailsRouteIndex] = useState(0);

  // Sync detailsRouteIndex with selectedRouteIndex when route is selected on map
  useEffect(() => {
    setDetailsRouteIndex(selectedRouteIndex);
  }, [selectedRouteIndex]);

  const sourceInputRef = useRef(null);
  const destInputRef = useRef(null);
  const lastPrefillDestinationRef = useRef('');

  // Map API mode to UI mode
  const apiToUiMode = (apiMode) => {
    const modeMap = { 'truck': 'truck', 'auto': 'car', 'motorcycle': 'two-wheeler', 'pedestrian': 'walk' };
    return modeMap[apiMode] || 'truck';
  };

  const [transportMode, setTransportMode] = useState(() => apiToUiMode(externalTravelMode));
  
  // Sync transportMode with external travelMode when it changes
  useEffect(() => {
    if (externalTravelMode) {
      setTransportMode(apiToUiMode(externalTravelMode));
    }
  }, [externalTravelMode]);

  // ============ UNIFIED WAYPOINTS SYSTEM ============
  // Waypoints array: [source, ...stops, destination]
  // Each waypoint has: { id, name, location, type: 'source'|'stop'|'destination' }
  const [waypoints, setWaypointsInternal] = useState([
    { id: 'waypoint-source', name: '', location: null, type: 'source' },
    { id: 'waypoint-destination', name: '', location: null, type: 'destination' }
  ]);

  // Ensure all waypoints have unique IDs
  const setWaypoints = useCallback((newWaypointsOrUpdater) => {
    if (typeof newWaypointsOrUpdater === 'function') {
      // It's an updater function
      setWaypointsInternal(prev => {
        const newWaypoints = newWaypointsOrUpdater(prev);
        return newWaypoints.map((wp, index) => ({
          ...wp,
          id: wp.id || `waypoint-${Date.now()}-${index}-${Math.random().toString(36).substr(2, 9)}`
        }));
      });
    } else {
      // It's a direct value
      const waypointsWithIds = newWaypointsOrUpdater.map((wp, index) => ({
        ...wp,
        id: wp.id || `waypoint-${Date.now()}-${index}-${Math.random().toString(36).substr(2, 9)}`
      }));
      setWaypointsInternal(waypointsWithIds);
    }
  }, []);

  // Derived state for backwards compatibility
  const sourceName = waypoints[0]?.name || '';
  const destName = waypoints[waypoints.length - 1]?.name || '';
  const stops = waypoints.slice(1, -1); // Everything between source and destination

  // Update source name
  const setSourceName = useCallback((name) => {
    setWaypoints(prev => {
      const updated = [...prev];
      updated[0] = { ...updated[0], name };
      return updated;
    });
  }, [setWaypoints]);

  // Update destination name
  const setDestName = useCallback((name) => {
    setWaypoints(prev => {
      const updated = [...prev];
      updated[updated.length - 1] = { ...updated[updated.length - 1], name };
      return updated;
    });
  }, [setWaypoints]);

  // Legacy setStops for compatibility - updates middle waypoints
  const setStops = useCallback((newStops) => {
    setWaypoints(prev => {
      const source = prev[0];
      const destination = prev[prev.length - 1];
      const stopsWithIds = newStops.map((stop, index) => ({
        ...stop,
        id: stop.id || `waypoint-stop-${Date.now()}-${index}-${Math.random().toString(36).substr(2, 9)}`,
        type: 'stop'
      }));
      return [source, ...stopsWithIds, destination];
    });
  }, [setWaypoints]);

  const [dragIndex, setDragIndex] = useState(null);
  // Flag to prevent useEffects from overwriting waypoints after a drag reorder
  const isDragReorderingRef = useRef(false);
  const [showAvoidDropdown, setShowAvoidDropdown] = useState(false); // eslint-disable-line no-unused-vars
  const [avoidOptions, setAvoidOptions] = useState({ tolls: true, highways: false, shortestPath: true });
  const [stopResults, setStopResults] = useState([]);
  const [stopLoading] = useState(false);
  const [activeStopIndex, setActiveStopIndex] = useState(null);
  const [showStopDropdown, setShowStopDropdown] = useState(false);
  const [stopSelectedIndex, setStopSelectedIndex] = useState(0);
  const stopInputRefs = useRef([]);
  
  // dnd-kit sensors
  const sensors = useSensors(
    useSensor(PointerSensor, {
      activationConstraint: {
        distance: 5, // 5px movement required before drag starts
      },
    }),
    useSensor(KeyboardSensor, {
      coordinateGetter: sortableKeyboardCoordinates,
    })
  );

  const handleDragEnd = (event) => {
    const { active, over } = event;
    
    if (active && over && active.id !== over.id) {
      const oldIndex = waypoints.findIndex(w => w.id === active.id);
      const newIndex = waypoints.findIndex(w => w.id === over.id);
      
      if (oldIndex !== -1 && newIndex !== -1 && oldIndex !== newIndex) {
        const newWaypoints = arrayMove(waypoints, oldIndex, newIndex);
        
        // Update waypoint types based on new positions
        const updatedWaypoints = newWaypoints.map((wp, idx) => ({
          ...wp,
          type: idx === 0 ? 'source' : (idx === newWaypoints.length - 1 ? 'destination' : 'stop')
        }));
        
        // Prevent useEffects from overwriting our reordered waypoints
        // when the parent state updates propagate back
        isDragReorderingRef.current = true;
        
        setWaypoints(updatedWaypoints);
        
        // If "Your Location" moved away from index 0, mark source as user-modified
        // so the auto-prefill effect doesn't re-inject it
        setSourceCleared(true);
        
        // Sync with parent component
        syncWaypointsToParent(updatedWaypoints);
        
        // Clear the flag after parent state has had time to propagate back
        setTimeout(() => {
          isDragReorderingRef.current = false;
        }, 500);
      }
    }
    
    setDragIndex(null);
  };

  // Handle drag over to track position
  const handleDragOver = () => {};

  // Sync waypoints array back to parent's expected format
  const syncWaypointsToParent = useCallback((waypointsList) => {
    if (!waypointsList || waypointsList.length < 2) return;
    
    const source = waypointsList[0];
    const destination = waypointsList[waypointsList.length - 1];
    const middleStops = waypointsList.slice(1, -1);
    
    // Get stops with locations
    const stopsWithLocations = middleStops
      .filter(s => s.location)
      .map(s => ({ ...s.location, name: s.name, shortName: s.shortName || s.name }));
    
    // Use a single atomic 'reorder' action to update everything at once
    // This prevents double fetchRoute calls and race conditions
    onRoutePointsChange('reorder', {
      source: source.location ? {
        location: source.location,
        details: {
          entity_name: source.shortName || source.name,
          display_text: source.name,
          display_name: source.name,
          name: source.shortName || source.name,
          location: source.location
        }
      } : null,
      destination: destination.location ? {
        location: destination.location,
        details: {
          entity_name: destination.shortName || destination.name,
          display_text: destination.name,
          display_name: destination.name,
          name: destination.shortName || destination.name,
          location: destination.location
        }
      } : null,
      stops: stopsWithLocations
    });
  }, [onRoutePointsChange]);

  // ============ UNIFIED WAYPOINT HANDLERS ============
  const [activeWaypointIndex, setActiveWaypointIndex] = useState(null);
  const [waypointResults, setWaypointResults] = useState([]);
  const [waypointSelectedIndex, setWaypointSelectedIndex] = useState(0);
  const waypointTimeoutRef = useRef(null);

  const handleWaypointChange = useCallback((e, index) => {
    const query = e.target.value;
    
    // If editing the source (index 0), mark it as cleared to prevent auto-fill with "Your Location"
    if (index === 0) {
      setSourceCleared(true);
    }
    
    // Update the waypoint name
    setWaypoints(prev => {
      const updated = [...prev];
      updated[index] = { ...updated[index], name: query, location: null }; // Clear location when typing
      return updated;
    });
    
    setActiveWaypointIndex(index);
    setWaypointSelectedIndex(0);

    if (waypointTimeoutRef.current) clearTimeout(waypointTimeoutRef.current);

    if (!query.trim()) {
      setWaypointResults([]);
      return;
    }

    if (query.trim().length >= SEARCH_CONFIG.MIN_QUERY_LENGTH) {
      waypointTimeoutRef.current = setTimeout(async () => {
        const results = await performSearch(query);
        setWaypointResults(results);
      }, SEARCH_CONFIG.DEBOUNCE_DELAY);
    } else {
      setWaypointResults([]);
    }
  // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [setWaypoints]);

  const handleWaypointSelect = useCallback((result, index) => {
    const name = getAddressDisplay(result);
    const shortName = result.entity_name || result.rooftop_name || result.name || name;
    const location = {
      lat: parseFloat(result.location?.lat ?? result.centroid_point?.lat ?? result.lat),
      lng: parseFloat(result.location?.long ?? result.location?.lng ?? result.location?.lon ?? result.centroid_point?.lng ?? result.centroid_point?.long ?? result.lng ?? result.lon ?? result.long),
    };
    
    // Update waypoint state and sync to parent
    setWaypoints(prev => {
      const updated = [...prev];
      updated[index] = { ...updated[index], name, shortName, location };
      
      // Determine if this is source, destination, or stop based on updated array
      const isSource = index === 0;
      const isDestination = index === updated.length - 1;
      
      if (isSource) {
        // Source selected - call onRoutePointsChange directly
        onRoutePointsChange('A', location, {
          entity_name: shortName,
          display_text: name,
          display_name: name,
          name: shortName,
          location: location
        });
      } else if (isDestination) {
        // Destination selected - call onRoutePointsChange and sync stops
        onRoutePointsChange('B', location, {
          entity_name: shortName,
          display_text: name,
          display_name: name,
          name: shortName,
          location: location
        });
        // Also sync stops in case there are middle waypoints
        const middleStops = updated.slice(1, -1);
        const stopsWithLocations = middleStops
          .filter(s => s.location)
          .map(s => ({ ...s.location, name: s.name, shortName: s.shortName || s.name }));
        if (onStopsChange) {
          onStopsChange(stopsWithLocations);
        }
      } else {
        // Stop selected - sync all waypoints
        setTimeout(() => {
          syncWaypointsToParent(updated);
        }, 0);
      }
      
      return updated;
    });
    
    setWaypointResults([]);
    setActiveWaypointIndex(null);

    // Auto-focus the next empty input after selection
    setTimeout(() => {
      const isSource = index === 0;
      const isDestination = index === waypoints.length - 1;
      if (isSource && !waypoints[waypoints.length - 1]?.location) {
        destInputRef.current?.focus();
      } else if (isDestination && !waypoints[0]?.location) {
        sourceInputRef.current?.focus();
      }
    }, 50);
  }, [setWaypoints, syncWaypointsToParent, onRoutePointsChange, onStopsChange, waypoints]);

  // Expose setWaypointLocation to parent via ref for map click handling
  useImperativeHandle(ref, () => ({
    setWaypointLocation: (index, location, name) => {
      handleWaypointSelect({
        entity_name: name,
        display_name: name,
        name: name,
        location: { lat: location.lat, long: location.lng, lng: location.lng }
      }, index);
    },
    resetWaypoints: () => {
      setWaypoints([
        { id: 'waypoint-source', name: '', location: null, type: 'source' },
        { id: 'waypoint-destination', name: '', location: null, type: 'destination' }
      ]);
    }
  }), [handleWaypointSelect, setWaypoints]);

  const handleWaypointKeyDown = useCallback((e, index) => {
    if (e.key === 'ArrowDown') {
      e.preventDefault();
      if (waypointResults.length > 0) {
        setWaypointSelectedIndex(prev => (prev + 1) % waypointResults.length);
      }
    } else if (e.key === 'ArrowUp') {
      e.preventDefault();
      if (waypointResults.length > 0) {
        setWaypointSelectedIndex(prev => (prev - 1 + waypointResults.length) % waypointResults.length);
      }
    } else if (e.key === 'Enter') {
      e.preventDefault();
      if (waypointResults.length > 0) {
        handleWaypointSelect(waypointResults[waypointSelectedIndex], index);
      }
    }
  }, [waypointResults, waypointSelectedIndex, handleWaypointSelect]);

  const waypointFocusCounterRef = useRef(0);

  const handleWaypointFocus = useCallback((index) => {
    waypointFocusCounterRef.current += 1;
    setActiveWaypointIndex(index);
    setActiveInput(index === 0 ? 'source' : (index === waypoints.length - 1 ? 'dest' : 'stop'));
    if (onActiveInputChange) {
      onActiveInputChange(index === 0 ? 'source' : (index === waypoints.length - 1 ? 'dest' : 'stop'), index);
    }
  }, [waypoints.length, onActiveInputChange]);

  const handleWaypointBlur = useCallback(() => {
    const counterAtBlur = waypointFocusCounterRef.current;
    // Delay to allow click on dropdown or another input to take focus
    setTimeout(() => {
      // Only clear if no new focus happened since this blur
      if (waypointFocusCounterRef.current === counterAtBlur) {
        setWaypointResults([]);
        setActiveWaypointIndex(null);
        setActiveInput(null);
        if (onActiveInputChange) {
          onActiveInputChange(null, null);
        }
      }
    }, 200);
  }, [onActiveInputChange]);

  const handleRemoveWaypoint = useCallback((index) => {
    if (waypoints.length <= 2) return; // Keep at least source and destination
    
    const updated = waypoints.filter((_, i) => i !== index);
    setWaypoints(updated);
    
    // Sync to parent after state update to trigger route recalculation
    setTimeout(() => {
      syncWaypointsToParent(updated);
    }, 0);
  }, [waypoints, setWaypoints, syncWaypointsToParent]);

  const MAX_STOPS = 9;

  const pendingFocusRef = useRef(false);

  const handleAddWaypoint = useCallback(() => {
    setWaypoints(prev => {
      const currentStops = prev.filter(wp => wp.type === 'stop');
      if (currentStops.length >= MAX_STOPS) return prev;
      const newWaypoint = {
        id: `waypoint-${Date.now()}-${Math.random().toString(36).substr(2, 9)}`,
        name: '',
        location: null,
        type: 'stop'
      };
      pendingFocusRef.current = true;
      // Add at the end of the list
      return [...prev, newWaypoint];
    });
  }, [setWaypoints]);

  // Auto-focus newly added stop input after waypoints change
  useEffect(() => {
    if (pendingFocusRef.current) {
      pendingFocusRef.current = false;
      // Try destInputRef first, then fall back to finding the last input in the container
      setTimeout(() => {
        if (destInputRef.current) {
          destInputRef.current.focus();
        } else {
          // Fallback: find the last route-search-input-new in the DOM
          const inputs = document.querySelectorAll('.route-search-input-new');
          if (inputs.length > 0) {
            inputs[inputs.length - 1].focus();
          }
        }
      }, 150);
    }
  }, [waypoints]);

  const handleDragStart = (event) => {
    const { active } = event;
    const index = waypoints.findIndex(w => w.id === active.id);
    setDragIndex(index);
  };

  const handleStopSelect = (result, index) => {
    const updated = [...stops];
    updated[index].name = getAddressDisplay(result);
    updated[index].location = {
      lat: parseFloat(result.location?.lat ?? result.centroid_point?.lat ?? result.lat),
      lng: parseFloat(result.location?.long ?? result.location?.lng ?? result.location?.lon ?? result.centroid_point?.lng ?? result.centroid_point?.long ?? result.lng ?? result.lon ?? result.long),
    };
    setStops(updated);
    setShowStopDropdown(false);
    setActiveStopIndex(null);
    setStopResults([]);

    // Notify parent with updated stops that have locations
    const stopsWithLocations = updated
      .filter(s => s.location)
      .map(s => ({ ...s.location, name: s.name, shortName: s.shortName || s.name }));
    if (onStopsChange) {
      onStopsChange(stopsWithLocations);
    }
  };

  // Close dropdowns when clicking outside
  useEffect(() => {
    const handleClickOutside = (e) => {
      if (!e.target.closest('.route-details-panel')) {
        setShowSourceDropdown(false);
        setShowDestDropdown(false);
        setShowStopDropdown(false);
      }
    };
    document.addEventListener('click', handleClickOutside);
    return () => document.removeEventListener('click', handleClickOutside);
  }, []);

  // Reset state when panel is closed
  useEffect(() => {
    if (!isRoutingMode) {
      // Reset waypoints to initial state
      setWaypointsInternal([
        { id: 'waypoint-source', name: '', location: null, type: 'source' },
        { id: 'waypoint-destination', name: '', location: null, type: 'destination' }
      ]);
      setSourceResults([]);
      setDestResults([]);
      setActiveInput(null);
      setShowSourceDropdown(false);
      setShowDestDropdown(false);
      setShowStopDropdown(false);
      setSourceCleared(false);
      setStopResults([]);
      setActiveStopIndex(null);
      setWaypointResults([]);
      setActiveWaypointIndex(null);
      lastPrefillDestinationRef.current = '';
    }
  }, [isRoutingMode]);

  useEffect(() => {
    if (isRoutingMode && isMweb) {
      setSheetHeight(40);
    }
  }, [isRoutingMode, isMweb]);

  const handleTouchStart = (e) => {
    startY.current = e.touches[0].clientY;
    startHeight.current = sheetHeight;
  };

  const handleTouchMove = (e) => {
    if (!startY.current) return;
    const currentY = e.touches[0].clientY;
    const deltaY = currentY - startY.current;
    const windowHeight = window.innerHeight;
    const deltaVh = (deltaY / windowHeight) * 100;
    let newHeight = startHeight.current - deltaVh;
    if (newHeight < 35) newHeight = 35;
    if (newHeight > 85) newHeight = 85;
    setSheetHeight(newHeight);
  };

  const handleTouchEnd = () => {
    startY.current = null;
    startHeight.current = null;
  };

  const panelStyle = isMweb ? {
    height: `${sheetHeight}vh`,
    maxHeight: 'calc(100vh - 140px)',
    transition: startY.current ? 'none' : 'height 0.3s ease-out'
  } : {};

  // Auto-focus input when Directions tab opens
  useEffect(() => {
    if (!isRoutingMode) return;
    // If source is already filled (e.g. user's current location), focus destination directly
    const shouldFocusDest = focusTarget === 'dest' || routePointA;
    const nextActive = shouldFocusDest ? 'dest' : 'source';
    const targetRef = shouldFocusDest ? destInputRef : sourceInputRef;
    const waypointIndex = shouldFocusDest ? waypoints.length - 1 : 0;
    setActiveInput(nextActive);
    if (onActiveInputChange) {
      onActiveInputChange(nextActive, waypointIndex);
    }
    // Small delay to ensure DOM is ready
    setTimeout(() => {
      targetRef.current?.focus();
    }, 100);
  // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [isRoutingMode, focusTarget, onActiveInputChange, routePointA]);

  const getAddressDisplay = (details = {}) => {
    return details.display_text || details.entity_name || details.display_name || details.name || '';
  };

  // Update source/dest names when route details change
  useEffect(() => {
    if (isDragReorderingRef.current) return;
    if (routeDetails?.pointADetails && !sourceCleared) {
      setSourceName(getAddressDisplay(routeDetails.pointADetails));
    }
    if (routeDetails?.pointBDetails) {
      setDestName(getAddressDisplay(routeDetails.pointBDetails));
    }
  }, [routeDetails, sourceCleared, setSourceName, setDestName]);

  useEffect(() => {
    if (isDragReorderingRef.current) return;
    if (routePointADetails && !sourceCleared) {
      const name = getAddressDisplay(routePointADetails);
      const shortName = routePointADetails.entity_name || routePointADetails.name || name;
      setWaypoints(prev => {
        const updated = [...prev];
        updated[0] = { 
          ...updated[0], 
          name,
          shortName,
          location: routePointA || updated[0].location
        };
        return updated;
      });
    }
  }, [routePointADetails, routePointA, setWaypoints, sourceCleared]);

  useEffect(() => {
    if (isDragReorderingRef.current) return;
    if (routePointBDetails) {
      const name = getAddressDisplay(routePointBDetails);
      const shortName = routePointBDetails.entity_name || routePointBDetails.name || name;
      setWaypoints(prev => {
        const updated = [...prev];
        updated[updated.length - 1] = { 
          ...updated[updated.length - 1], 
          name,
          shortName,
          location: routePointB || updated[updated.length - 1].location
        };
        return updated;
      });
    }
  }, [routePointBDetails, routePointB, setWaypoints]);

  // Prefill source with current location on open (unless user cleared it)
  useEffect(() => {
    if (!isRoutingMode) return;
    if (sourceCleared) return;
    if (routePointA) return;
    if (!userLocation) return;
    if (sourceName.trim()) return;
    // Also check if waypoint already has a location set
    if (waypoints[0]?.location) return;

    const locationLabel = 'Your Location';
    // Update waypoint
    setWaypoints(prev => {
      const updated = [...prev];
      updated[0] = { 
        ...updated[0], 
        name: locationLabel,
        location: userLocation
      };
      return updated;
    });
    
    onRoutePointsChange('A', userLocation, {
      entity_name: locationLabel,
      display_name: locationLabel,
      name: locationLabel,
      location: { lat: userLocation.lat, lon: userLocation.lng, long: userLocation.lng }
    });
  }, [isRoutingMode, sourceCleared, routePointA, userLocation, sourceName, onRoutePointsChange, setWaypoints, waypoints]);

  useEffect(() => {
    if (!isRoutingMode) {
      return;
    }
    if (!prefillDestinationName || routeDetails?.pointBDetails) {
      return;
    }
    if (!destName.trim() || destName === lastPrefillDestinationRef.current) {
      setDestName(prefillDestinationName);
      lastPrefillDestinationRef.current = prefillDestinationName;
    }
  }, [isRoutingMode, prefillDestinationName, routeDetails, destName, setDestName]);

  const performSearch = async (query) => {
    if (query.trim().length < SEARCH_CONFIG.MIN_QUERY_LENGTH) return [];

    try {
      // Get map center for lat/lng
      const center = mapRef.current?.getCenter();
      const lat = center ? parseFloat(center.lat).toFixed(6) : '28.6304';
      const lng = center ? parseFloat(center.lng).toFixed(6) : '77.1277';

      const results = await searchLocation(query, lat, lng);
      return results;
    } catch (error) {
      // Search error
      return [];
    }
  };

  const handleSourceSelect = (result) => {
    // Show the best available name in the input
    setSourceName(getAddressDisplay(result));
    setShowSourceDropdown(false);
    setSourceCleared(false);
    const point = {
      lat: parseFloat(result.location?.lat ?? result.centroid_point?.lat ?? result.lat),
      lng: parseFloat(result.location?.long ?? result.location?.lng ?? result.location?.lon ?? result.centroid_point?.lng ?? result.centroid_point?.long ?? result.lng ?? result.lon ?? result.long),
    };
    onRoutePointsChange('A', point, result);

    // After selecting source, move focus to destination if it's empty
    if (!destName.trim() || !routePointB) {
      setTimeout(() => {
        destInputRef.current?.focus();
      }, 100);
    }
  };

  const handleDestSelect = (result) => {
    // Show the best available name in the input
    setDestName(getAddressDisplay(result));
    setShowDestDropdown(false);
    const point = {
      lat: parseFloat(result.location?.lat ?? result.centroid_point?.lat ?? result.lat),
      lng: parseFloat(result.location?.long ?? result.location?.lng ?? result.location?.lon ?? result.centroid_point?.lng ?? result.centroid_point?.long ?? result.lng ?? result.lon ?? result.long),
    };
    onRoutePointsChange('B', point, result);

    // After selecting destination, move focus to source if it's empty
    if (!sourceName.trim() || !routePointA) {
      setTimeout(() => {
        sourceInputRef.current?.focus();
      }, 100);
    }
  };



  const renderContent = () => {
    const showExplorePlaces = false;
    // Determine which results to show based on active input
    const activeResults = activeInput === 'source' ? sourceResults : (activeInput === 'dest' ? destResults : []);
    const showResults = (activeInput === 'source')
      ? (showSourceDropdown && activeResults.length > 0)
      : (showDestDropdown && activeResults.length > 0);
    // Check if waypoint search results are showing (new unified system)
    const hasWaypointResults = activeWaypointIndex !== null && waypointResults.length > 0;
    const activeSelectedIndex = activeInput === 'source' ? sourceSelectedIndex : destSelectedIndex;
    const handleSelect = activeInput === 'source' ? handleSourceSelect : handleDestSelect;
    const setSelectedIndex = activeInput === 'source' ? setSourceSelectedIndex : setDestSelectedIndex;

    return (
      <>
        <div className="route-input-form">
          <div className="route-inputs-container-new">
            {/* All waypoints in a single draggable list */}
            <div className={`route-inputs-with-line${dragIndex !== null ? ' is-dragging' : ''}`}>
              <DndContext
                sensors={sensors}
                collisionDetection={closestCenter}
                onDragStart={handleDragStart}
                onDragOver={handleDragOver}
                onDragEnd={handleDragEnd}
                modifiers={[restrictToVerticalAxis, restrictToParentElement]}
              >
                <SortableContext
                  items={waypoints.map(w => w.id)}
                  strategy={verticalListSortingStrategy}
                >
                  {waypoints.map((waypoint, index) => (
                    <DraggableWaypoint
                      key={waypoint.id}
                      waypoint={waypoint}
                      index={index}
                      totalCount={waypoints.length}
                      inputRef={index === 0 ? sourceInputRef : (index === waypoints.length - 1 ? destInputRef : (el) => (stopInputRefs.current[index - 1] = el))}
                      onInputChange={(e) => handleWaypointChange(e, index)}
                      onInputKeyDown={(e) => handleWaypointKeyDown(e, index)}
                      onInputFocus={() => handleWaypointFocus(index)}
                      onInputBlur={() => handleWaypointBlur(index)}
                      onRemove={() => handleRemoveWaypoint(index)}
                      isLoading={index === 0 ? sourceLoading : (index === waypoints.length - 1 ? destLoading : stopLoading)}
                      isActive={activeWaypointIndex === index}
                      showDropdown={false}
                      searchResults={[]}
                      selectedResultIndex={0}
                      onResultSelect={() => {}}
                      setSelectedResultIndex={() => {}}
                      isFirst={index === 0}
                      isLast={index === waypoints.length - 1}
                    />
                  ))}
                </SortableContext>
              </DndContext>
            </div>

            {/* Right side: Swap Button - hidden when there are stops */}
            {waypoints.length === 2 && (
            <div className="route-swap-column">
              <button
                className="route-swap-btn-new"
                onClick={() => {
                  // Get current source and destination
                  const currentSource = waypoints[0];
                  const currentDest = waypoints[waypoints.length - 1];
                  
                  if (!currentSource?.location || !currentDest?.location) return;
                  
                  // Swap waypoints in local state
                  setWaypoints(prev => {
                    if (prev.length < 2) return prev;
                    const updated = [...prev];
                    const temp = updated[0];
                    updated[0] = { ...updated[updated.length - 1], type: 'source' };
                    updated[updated.length - 1] = { ...temp, type: 'destination' };
                    return updated;
                  });
                  
                  // Call the swap operation in parent with current values
                  onRoutePointsChange('swap', {
                    newA: currentDest?.location,
                    newB: currentSource?.location,
                    detailsA: currentDest ? {
                      entity_name: currentDest.name,
                      display_name: currentDest.name,
                      name: currentDest.name,
                      location: currentDest.location
                    } : null,
                    detailsB: currentSource ? {
                      entity_name: currentSource.name,
                      display_name: currentSource.name,
                      name: currentSource.name,
                      location: currentSource.location
                    } : null
                  });
                }}
                title="Swap locations"
                disabled={!waypoints[0]?.location && !waypoints[waypoints.length - 1]?.location}
              >
                <svg width="36" height="36" viewBox="0 0 36 36" fill="none" xmlns="http://www.w3.org/2000/svg" style={{ opacity: 1 }}>
                  <path d="M0 18C0 8.05887 8.05887 0 18 0V0C27.9411 0 36 8.05887 36 18V18C36 27.9411 27.9411 36 18 36V36C8.05887 36 0 27.9411 0 18V18Z" fill="#000"/>
                  <g clipPath="url(#clip0_3346_16169)">
                    <g clipPath="url(#clip1_3346_16169)">
                      <mask id="mask0_3346_16169" style={{ maskType: 'alpha' }} maskUnits="userSpaceOnUse" x="6" y="6" width="24" height="24">
                        <rect x="6" y="6" width="24" height="24" fill="#D9D9D9"/>
                      </mask>
                      <g mask="url(#mask0_3346_16169)">
                        <path d="M14 19V11.825L11.425 14.4L10 13L15 8L20 13L18.575 14.4L16 11.825V19H14ZM21 28L16 23L17.425 21.6L20 24.175V17H22V24.175L24.575 21.6L26 23L21 28Z" fill="white"/>
                      </g>
                    </g>
                  </g>
                  <defs>
                    <clipPath id="clip0_3346_16169">
                      <rect width="20" height="20" fill="white" transform="translate(8 8)"/>
                    </clipPath>
                    <clipPath id="clip1_3346_16169">
                      <rect width="24" height="24" fill="white" transform="translate(6 6)"/>
                    </clipPath>
                  </defs>
                </svg>
              </button>
            </div>
            )}
          </div>

          {/* Source/Dest Search Results - Rendered outside inputs container */}
          {((activeInput === 'source' && showSourceDropdown && sourceResults.length > 0) || 
            (activeInput === 'dest' && showDestDropdown && destResults.length > 0)) && (
            <>
              {/* Divider line before search results */}
              <div style={{ 
                height: '1px', 
                backgroundColor: '#E6E6E6', 
                marginTop: '24px', 
                marginBottom: '20px',
                marginLeft: '-16px',
                marginRight: '-16px',
                width: 'calc(100% + 32px)'
              }} />
              <div className="route-results-list">
                {(activeInput === 'source' ? sourceResults : destResults).map((result, idx) => (
                  <div
                    key={idx}
                    className={`search-result-item ${idx === (activeInput === 'source' ? sourceSelectedIndex : destSelectedIndex) ? 'keyboard-selected' : ''}`}
                    onMouseDown={(event) => {
                      event.preventDefault();
                      if (activeInput === 'source') {
                        handleSourceSelect(result);
                      } else {
                        handleDestSelect(result);
                      }
                    }}
                    onMouseEnter={() => {
                      if (activeInput === 'source') {
                        setSourceSelectedIndex(idx);
                      } else {
                        setDestSelectedIndex(idx);
                      }
                    }}
                  >
                    <div className="search-result-icon-container">
                      <img
                        src="/pin/_v1searchMarkerPin.png"
                        alt="location"
                        className="search-result-icon"
                      />
                    </div>
                    <div className="search-result-content">
                      <div className="search-result-heading">
                        {result.entity_name || result.rooftop_name || result.building_name || result.name}
                      </div>
                      <div className="search-result-subheading">
                        {result.display_text || result.entity_address || result.clean_address || result.address}
                      </div>
                    </div>
                  </div>
                ))}
              </div>
            </>
          )}

          {/* Waypoint Search Results - Rendered outside inputs container */}
          {activeWaypointIndex !== null && waypointResults.length > 0 && (
            <>
              {/* Divider line before search results */}
              <div style={{ 
                height: '1px', 
                backgroundColor: '#E6E6E6', 
                marginTop: '8px', 
                marginBottom: '8px',
                marginLeft: '-16px',
                marginRight: '-16px',
                width: 'calc(100% + 32px)'
              }} />
              <div className="route-results-list route-stop-results">
                {waypointResults.map((result, idx) => (
                  <div
                    key={idx}
                    className={`search-result-item ${idx === waypointSelectedIndex ? 'keyboard-selected' : ''}`}
                    onMouseDown={(event) => {
                      event.preventDefault();
                      handleWaypointSelect(result, activeWaypointIndex);
                    }}
                    onMouseEnter={() => setWaypointSelectedIndex(idx)}
                  >
                    <div className="search-result-icon-container">
                      <img
                        src="/pin/_v1searchMarkerPin.png"
                        alt="location"
                        className="search-result-icon"
                      />
                    </div>
                    <div className="search-result-content">
                      <div className="search-result-heading">
                        {result.entity_name || result.rooftop_name || result.building_name || result.name}
                      </div>
                      <div className="search-result-subheading">
                        {result.display_text || result.entity_address || result.clean_address || result.address}
                      </div>
                    </div>
                  </div>
                ))}
              </div>
            </>
          )}

          {/* Stop Search Results - For added stops */}
          {showStopDropdown && activeStopIndex !== null && stopResults.length > 0 && (
            <>
              {/* Divider line before search results */}
              <div style={{ 
                height: '1px', 
                backgroundColor: '#E6E6E6', 
                marginTop: '24px', 
                marginBottom: '20px',
                marginLeft: '-16px',
                marginRight: '-16px',
                width: 'calc(100% + 32px)'
              }} />
              <div className="route-results-list route-stop-results">
                {stopResults.map((result, idx) => (
                  <div
                    key={idx}
                    className={`search-result-item ${idx === stopSelectedIndex ? 'keyboard-selected' : ''}`}
                    onMouseDown={(event) => {
                      event.preventDefault();
                      handleStopSelect(result, activeStopIndex);
                    }}
                    onMouseEnter={() => setStopSelectedIndex(idx)}
                  >
                    <div className="search-result-icon-container">
                      <img
                        src="/pin/_v1searchMarkerPin.png"
                        alt="location"
                        className="search-result-icon"
                      />
                    </div>
                    <div className="search-result-content">
                      <div className="search-result-heading">
                        {result.entity_name || result.rooftop_name || result.building_name || result.name}
                      </div>
                      <div className="search-result-subheading">
                        {result.display_text || result.entity_address || result.clean_address || result.address}
                      </div>
                    </div>
                  </div>
                ))}
              </div>
            </>
          )}

          {/* Add Stop Row - Hide when showing search results */}
          {!(activeWaypointIndex !== null && waypointResults.length > 0) && !(showStopDropdown && activeStopIndex !== null && stopResults.length > 0) && (
          <div className="route-add-stop-row">
            <button className="route-add-stop-btn" onClick={handleAddWaypoint} disabled={waypoints.filter(wp => wp.type === 'stop').length >= MAX_STOPS || waypoints.some(wp => !wp.name?.trim())}>
              <img src="/icons/add-circle-outline.png" alt="Add Stop" className="route-add-stop-icon" />
              <span className="route-add-stop-text">Add Stop</span>
            </button>
            <div className="route-avoid-wrapper">
              <button className="route-avoid-dropdown">
                <span>Options</span>
                <svg width="12" height="12" viewBox="0 0 12 12" fill="none" style={{ transform: showAvoidDropdown ? 'rotate(180deg)' : 'none', transition: 'transform 0.2s' }}><path d="M3 4L6 8L9 4Z" fill="#3B3B3B"/></svg>
              </button>
              {showAvoidDropdown && (
                <div className="route-avoid-menu">
                  <div className="route-avoid-menu-header">
                    <span>Options</span>
                    <svg width="12" height="12" viewBox="0 0 12 12" fill="none" style={{ transform: 'rotate(180deg)' }}><path d="M3 4L6 8L9 4Z" fill="#3B3B3B"/></svg>
                  </div>
                  {[
                    { key: 'tolls', label: 'Avoid Tolls' },
                    { key: 'highways', label: 'Avoid Highways' },
                    { key: 'shortestPath', label: 'Shortest Path' },
                  ].map((opt) => (
                    <label key={opt.key} className="route-avoid-menu-item">
                      <input
                        type="checkbox"
                        checked={avoidOptions[opt.key]}
                        onChange={() => setAvoidOptions({ ...avoidOptions, [opt.key]: !avoidOptions[opt.key] })}
                        className="route-avoid-checkbox"
                      />
                      <span>{opt.label}</span>
                    </label>
                  ))}
                </div>
              )}
            </div>
            <button className="route-time-dropdown" disabled style={{ visibility: routePointB ? 'visible' : 'hidden' }}>
              <span>09:30 AM</span>
              <svg width="12" height="12" viewBox="0 0 12 12" fill="none"><path d="M3 4L6 8L9 4Z" fill="#3B3B3B"/></svg>
            </button>
          </div>
          )}

          {/* "Your location" button - shown when any input is focused and empty, 
              and the previous waypoint (if any) doesn't have "Your Location" */}
          {activeWaypointIndex !== null && !waypoints[activeWaypointIndex]?.name?.trim() && userLocation && 
            (activeWaypointIndex === 0 || waypoints[activeWaypointIndex - 1]?.name !== 'Your Location') && 
            waypointResults.length === 0 && (
            <div className="route-your-location-suggestion">
              <div
                className="route-your-location-btn"
                onMouseDown={(event) => {
                  event.preventDefault();
                  handleWaypointSelect({
                    entity_name: 'Your Location',
                    display_text: 'Your Location',
                    display_name: 'Your Location',
                    name: 'Your Location',
                    location: { lat: userLocation.lat, lon: userLocation.lng, long: userLocation.lng }
                  }, activeWaypointIndex);
                }}
              >
                <img
                  src="/icons/LocationSearchingFilled.png"
                  alt="Your location"
                  className="route-your-location-icon"
                />
                <span className="route-your-location-text">Your location</span>
              </div>
            </div>
          )}
        </div>

        {/* Loading State - Show whenever route is being calculated */}
        {routeDetailsLoading && (
          <div className="route-loading-container" style={{ marginTop: '20px' }}>
            <div className="route-loader">
              <div className="route-loader-spinner"></div>
              <div className="route-loader-text">Finding the best route...</div>
            </div>
          </div>
        )}

        {/* Divider line between Add Stop and Route Cards/Search Results */}
        {(routeDetails || showResults || hasWaypointResults) && !routeDetailsLoading && (
          <div className="route-section-divider" style={{ 
            height: '1px', 
            backgroundColor: '#E6E6E6', 
            marginTop: '24px', 
            marginBottom: '20px',
            marginLeft: '-16px',
            marginRight: '-16px',
            width: 'calc(100% + 32px)'
          }} />
        )}

        {/* Results List - Displayed below divider */}
        {showResults && (
          <>
            {/* Divider line before search results */}
            <div style={{ 
              height: '1px', 
              backgroundColor: '#E6E6E6', 
              marginTop: '24px', 
              marginBottom: '20px',
              marginLeft: '-16px',
              marginRight: '-16px',
              width: 'calc(100% + 32px)'
            }} />
            <div className="route-results-list">
            {activeResults.map((result, idx) => (
              <div
                key={idx}
                className={`search-result-item ${idx === activeSelectedIndex ? 'keyboard-selected' : ''}`}
                onMouseDown={(event) => {
                  event.preventDefault();
                  handleSelect(result);
                }}
                onMouseEnter={() => setSelectedIndex(idx)}
              >
                <div className="search-result-icon-container">
                  <img
                    src="/pin/_v1searchMarkerPin.png"
                    alt="location"
                    className="search-result-icon"
                  />
                </div>
                <div className="search-result-content">
                  <div className="search-result-heading">
                    {result.entity_name || result.rooftop_name || result.building_name || result.name}
                  </div>
                  <div className="search-result-subheading">
                    {result.display_text || result.entity_address || result.clean_address || result.address}
                  </div>
                </div>
              </div>
            ))}
            </div>
          </>
        )}

        {/* Route Info Card - Displayed when route is found and not loading or searching */}
        {routeDetails && !routeDetailsLoading && !showResults && !hasWaypointResults && (
          <>
            {/* Calculate which route is fastest */}
            {(() => {
              const allDurations = [
                { index: 0, duration: routeDetails.duration },
                ...(routeDetails.alternateRoutes || []).map((alt, i) => ({ index: i + 1, duration: alt.duration }))
              ];
              const fastestIndex = allDurations.reduce((min, curr) => 
                curr.duration < min.duration ? curr : min
              ).index;

              return (
                <>
                  <div 
                    className={`route-info-card ${selectedRouteIndex === 0 ? '' : 'route-info-card-alternate'}`}
                    onClick={() => onSelectRoute && onSelectRoute(0)}
                    style={{ cursor: 'pointer' }}
                  >
                    <div className="route-info-left">
                      <div className="route-info-icon">
                        <img 
                          src={transportMode === 'truck' 
                               ? (selectedRouteIndex === 0 ? '/icons/truckIcon.png' : '/icons/truckUnselected.png')
                               : transportMode === 'car' 
                                 ? (selectedRouteIndex === 0 ? '/icons/carSelected.png' : '/icons/carIcon.png')
                                 : transportMode === 'two-wheeler' ? '/icons/twoWheelerIcon.png' : 
                               '/icons/walkIcon.png'} 
                          alt="Vehicle" 
                          style={{ 
                            width: '24px', 
                            height: selectedRouteIndex === 0 
                              ? (transportMode === 'car' ? '28px' : '24px')
                              : (transportMode === 'truck' ? '28px' : '24px') 
                          }} 
                        />
                      </div>
                      <div className="route-info-main">
                        <div className="route-via-text">
                          {routeDetails.summary ? `via ${routeDetails.summary}` : 'via Fastest Route'}
                        </div>
                        {fastestIndex === 0 && <div className="route-fastest-tag">Fastest</div>}
                        <div className="route-details-link" onClick={(e) => { e.stopPropagation(); setDetailsRouteIndex(0); setShowRouteDetails(true); }}>Details</div>
                      </div>
                    </div>
                    <div className="route-info-stats">
                      <div className="route-stat-duration">{formatDuration(routeDetails.duration)}</div>
                      <div className="route-stat-distance">{formatDistance(routeDetails.distance)}</div>
                    </div>
                  </div>

                  {/* Alternate Routes */}
                  {routeDetails.alternateRoutes && routeDetails.alternateRoutes.length > 0 && (
                    <div className="alternate-routes-container">
                      {routeDetails.alternateRoutes.map((altRoute, index) => (
                        <div 
                          key={index} 
                          className={`route-info-card ${selectedRouteIndex === index + 1 ? '' : 'route-info-card-alternate'}`}
                          onClick={() => onSelectRoute && onSelectRoute(index + 1)}
                          style={{ cursor: 'pointer' }}
                        >
                          <div className="route-info-left">
                            <div className="route-info-icon">
                              <img 
                                src={transportMode === 'truck' 
                                     ? (selectedRouteIndex === index + 1 ? '/icons/truckIcon.png' : '/icons/truckUnselected.png')
                                     : transportMode === 'car' 
                                       ? (selectedRouteIndex === index + 1 ? '/icons/carSelected.png' : '/icons/carIcon.png')
                                       : transportMode === 'two-wheeler' ? '/icons/twoWheelerIcon.png' : 
                                     '/icons/walkIcon.png'} 
                                alt="Vehicle" 
                                style={{ 
                                  width: '24px', 
                                  height: selectedRouteIndex === index + 1 
                                    ? (transportMode === 'car' ? '28px' : '24px')
                                    : (transportMode === 'truck' ? '28px' : '24px') 
                                }} 
                              />
                            </div>
                            <div className="route-info-main">
                              <div className="route-via-text">
                                {altRoute.summary ? `via ${altRoute.summary}` : `via Route ${index + 2}`}
                              </div>
                              {fastestIndex === index + 1 && <div className="route-fastest-tag">Fastest</div>}
                              <div className="route-details-link" onClick={(e) => { e.stopPropagation(); setDetailsRouteIndex(index + 1); setShowRouteDetails(true); }}>Details</div>
                            </div>
                          </div>
                          <div className="route-info-stats">
                            <div className="route-stat-duration">{formatDuration(altRoute.duration)}</div>
                            <div className="route-stat-distance">{formatDistance(altRoute.distance)}</div>
                          </div>
                        </div>
                      ))}
                    </div>
                  )}

                  {/* Explore Places Section */}
                  {showExplorePlaces && (
                    <div className="explore-places-section">
                      <h4 className="explore-places-title">
                        Explore places along the route
                      </h4>
                      <div className="explore-places-grid">
                        <button className="explore-place-btn" onClick={() => onPoiClick && onPoiClick('food')}>
                          <div className="explore-icon-wrapper">
                            <img src="/buttons/_v1restrauntButton.png" alt="Restaurants" />
                          </div>
                          <span>Restaurants</span>
                        </button>
                        <button className="explore-place-btn" onClick={() => onPoiClick && onPoiClick('health')}>
                          <div className="explore-icon-wrapper">
                            <img src="/buttons/_v1healthButton.png" alt="Health" />
                          </div>
                          <span>Health</span>
                        </button>
                        <button className="explore-place-btn" onClick={() => onPoiClick && onPoiClick('education')}>
                          <div className="explore-icon-wrapper">
                            <img src="/buttons/_v1EducationButton.png" alt="Schools" />
                          </div>
                          <span>Schools</span>
                        </button>
                        <button className="explore-place-btn" onClick={() => onPoiClick && onPoiClick('finance')}>
                          <div className="explore-icon-wrapper">
                            <img src="/buttons/_v1currenyButton.png" alt="Banks" />
                          </div>
                          <span>Banks</span>
                        </button>
                        <button className="explore-place-btn" onClick={() => onPoiClick && onPoiClick('shopping')}>
                          <div className="explore-icon-wrapper">
                            <img src="/buttons/_v1shoppingButton.png" alt="Malls" />
                          </div>
                          <span>Malls</span>
                        </button>
                        <button className="explore-place-btn" onClick={() => onPoiClick && onPoiClick('services')}>
                          <div className="explore-icon-wrapper">
                            <img src="/buttons/_v1serviceButton.png" alt="Gas Stations" />
                          </div>
                          <span>Gas Stations</span>
                        </button>

                      </div>
                    </div>
                  )}
                </>
              );
            })()}
          </>
        )}


      </>
    );
  };

  const shouldShowPanel = isRoutingMode || (isMweb && (routeDetails || routePointA || routePointB || prefillDestinationName));
  if (!shouldShowPanel) return null;

  return (
    <div className="route-details-panel" style={panelStyle}>
      {isMobile && (
        <div
          className="sheet-handle-bar"
          onTouchStart={handleTouchStart}
          onTouchMove={handleTouchMove}
          onTouchEnd={handleTouchEnd}
          style={{
            width: '100%',
            height: '24px',
            display: 'flex',
            justifyContent: 'center',
            alignItems: 'center',
            cursor: 'grab',
            touchAction: 'none',
            borderTopLeftRadius: '20px',
            borderTopRightRadius: '20px',
            background: '#fff'
          }}
        >
          <div style={{
            width: '40px',
            height: '5px',
            backgroundColor: '#ccc',
            borderRadius: '3px'
          }}></div>
        </div>
      )}
      <div className="route-details-header">
        <h3>Directions</h3>
        <div style={{ display: 'flex', alignItems: 'center', gap: '12px' }}>
          <button
            className="route-details-close-btn"
            onClick={() => {/* share functionality */}}
            aria-label="Share route"
          >
            <img src="/icons/share.svg" alt="Share" style={{ width: '24px', height: '24px' }} />
          </button>
          <button
            className="route-details-close-btn"
            onClick={onClose}
            aria-label="Close route details"
          >
            <img src="/icons/Close.svg" alt="Close" style={{ width: '24px', height: '24px' }} />
          </button>
        </div>
      </div>

      {/* Transport Mode Buttons */}
      <div className="route-transport-modes">
        {[
          { id: 'truck', apiMode: 'truck', icon: '/icons/truckIcon.png', unselectedIcon: '/icons/truckUnselected.png', label: 'Truck', demoTime: '38 min' },
          { id: 'car', apiMode: 'auto', icon: '/icons/carSelected.png', unselectedIcon: '/icons/carIcon.png', label: 'Car', demoTime: '32 min' },
          { id: 'two-wheeler', apiMode: 'motorcycle', icon: '/icons/twoWheelerIcon.png', unselectedIcon: '/icons/twoWheelerIcon.png', label: 'Two Wheeler', demoTime: '28 min' },
          { id: 'walk', apiMode: 'pedestrian', icon: '/icons/walkIcon.png', unselectedIcon: '/icons/walkIcon.png', label: 'Walk', demoTime: '2 hr 15 min' },
        ].map((mode) => (
          <button
            key={mode.id}
            className={`route-transport-mode-btn${transportMode === mode.id ? ' active' : ''}`}
            onClick={() => {
              setTransportMode(mode.id);
              // Notify parent of travel mode change
              if (onTravelModeChange) {
                onTravelModeChange(mode.apiMode);
              }
              // Trigger route recalculation with the new travel mode
              if (onRoutePointsChange) {
                onRoutePointsChange('travelMode', mode.apiMode);
              }
            }}
            aria-label={mode.label}
          >
            <img 
              src={transportMode === mode.id ? mode.icon : mode.unselectedIcon} 
              alt={mode.label} 
              style={{ 
                width: '20px', 
                height: (transportMode !== mode.id && mode.id === 'truck') ? '24px' 
                      : (transportMode === mode.id && mode.id === 'car') ? '24px' 
                      : '20px' 
              }} 
            />
            {transportMode === mode.id && !routeDetailsLoading && routeDetails && (
              <span className="route-transport-mode-duration">
                {formatDuration(
                  selectedRouteIndex === 0
                    ? routeDetails.duration
                    : (routeDetails.alternateRoutes?.[selectedRouteIndex - 1]?.duration || routeDetails.duration)
                )}
              </span>
            )}
          </button>
        ))}
      </div>

      {renderContent()}

      {/* Route Details Step-by-Step Panel */}
      <RouteDetailsPanel
        routeData={
          detailsRouteIndex === 0
            ? routeDetails
            : routeDetails?.alternateRoutes?.[detailsRouteIndex - 1]
              ? {
                  ...routeDetails.alternateRoutes[detailsRouteIndex - 1],
                  pointADetails: routeDetails.pointADetails,
                  pointBDetails: routeDetails.pointBDetails
                }
              : null
        }
        isOpen={showRouteDetails}
        onClose={() => setShowRouteDetails(false)}
        isMobile={isMobile}
        onStepHover={onStepHover}
        onStepLeave={onStepLeave}
        onStepClick={onStepClick}
        stops={stops.filter(s => s.location).map(s => {
          // Use shortName (entity_name) as the place name (line 1)
          // Derive address by removing entity_name from the full name string (line 2)
          const fullName = s.name || '';
          const placeName = s.shortName || fullName.split(',')[0]?.trim() || '';
          let address = '';
          if (placeName && fullName.startsWith(placeName)) {
            // Remove entity_name from the beginning and trim leading comma/space
            address = fullName.slice(placeName.length).replace(/^[,\s]+/, '').trim();
          } else {
            // Fallback: everything after first comma
            const parts = fullName.split(',');
            address = parts.slice(1).map(p => p.trim()).join(', ');
          }
          return {
            name: placeName,
            shortName: placeName,
            address: address,
          };
        })}
      />
    </div>
  );
});

export default RoutingPanel;
